import { getController } from '@shared/util/timeout';
import {
  TOOLTIP_ATTRIBUTE,
  TOOLTIP_HOTKEY_ATTRIBUTE,
  TOOLTIP_POSITION_ATTRIBUTE,
} from './getTooltipProps';
import { TooltipPosition } from './types';

/** Approximates the native `title` delay, which browsers don't expose. */
export const TOOLTIP_SHOW_DELAY_MS = 500;
/** How long after a tooltip hides that the next one still shows instantly. */
export const TOOLTIP_WARM_WINDOW_MS = 300;
/** How often a showing or pending trigger is checked for being hidden by CSS. */
export const TOOLTIP_VISIBILITY_CHECK_INTERVAL_MS = 100;

export interface TooltipTrigger {
  element: HTMLElement;
  text: string;
  position: TooltipPosition;
  hotkey: string | null;
}

interface TooltipCallbacks {
  /** Called to show a trigger, and again when the showing trigger's attributes change. */
  onShow: (trigger: TooltipTrigger) => void;
  onHide: () => void;
}

const positions: TooltipPosition[] = ['top', 'bottom', 'left', 'right'];
const triggerSelector = `[${TOOLTIP_ATTRIBUTE}]`;

/**
 * Watches the document for hovers over `data-tooltip` elements (see `getTooltipProps`)
 * and decides when a tooltip shows and hides. It only reports; rendering is
 * `TooltipPresenter`'s job. Returns a cleanup function.
 */
export function observeTooltips({ onShow, onHide }: TooltipCallbacks) {
  const { signal, abort } = getController();

  let candidate: HTMLElement | null = null;
  let shown = false;
  let lastHiddenAt = -Infinity;
  let showTimer: number | undefined;
  let connectedCheck: number | undefined;
  let triggerObserver: MutationObserver | undefined;

  function onPointerOver(event: PointerEvent) {
    if (event.pointerType === 'touch') {
      return;
    }
    setCandidate(getTrigger(event.target));
  }

  function onPointerOut(event: PointerEvent) {
    // Leaving the window has no `pointerover` to follow it.
    if (!event.relatedTarget) {
      setCandidate(null);
    }
  }

  function onScroll(event: Event) {
    const { target } = event;
    if (
      candidate &&
      (target === document || (target instanceof Node && target.contains(candidate)))
    ) {
      setCandidate(null);
    }
  }

  function setCandidate(trigger: HTMLElement | null) {
    if (trigger === candidate) {
      return;
    }

    const warm = shown || performance.now() - lastHiddenAt < TOOLTIP_WARM_WINDOW_MS;
    detach();

    if (!trigger) {
      hide();
      return;
    }

    attach(trigger);
    if (warm) {
      show();
    } else {
      showTimer = window.setTimeout(show, TOOLTIP_SHOW_DELAY_MS);
    }
  }

  function show() {
    const trigger = candidate && readTrigger(candidate);
    if (trigger) {
      shown = true;
      onShow(trigger);
    } else {
      hide();
    }
  }

  function hide() {
    if (shown) {
      shown = false;
      lastHiddenAt = performance.now();
      onHide();
    }
  }

  function attach(trigger: HTMLElement) {
    candidate = trigger;

    // A trigger removed from under the pointer never gets a `pointerout`. Watching the document
    // catches the removal before the next paint; left for the interval below, the tooltip would
    // outlive its anchor for a frame or more, which Safari draws in the top-left corner.
    triggerObserver = new MutationObserver(records => {
      if (!trigger.isConnected || !trigger.hasAttribute(TOOLTIP_ATTRIBUTE)) {
        setCandidate(null);
      } else if (shown && records.some(record => record.type === 'attributes')) {
        show();
      }
    });
    triggerObserver.observe(trigger, {
      attributes: true,
      attributeFilter: [TOOLTIP_ATTRIBUTE, TOOLTIP_POSITION_ATTRIBUTE, TOOLTIP_HOTKEY_ATTRIBUTE],
    });
    triggerObserver.observe(document, { childList: true, subtree: true });

    // Hidden by CSS (a closed menu, `display: none`) changes no DOM, so poll for that.
    connectedCheck = window.setInterval(() => {
      if (!isPresent(trigger)) {
        setCandidate(null);
      }
    }, TOOLTIP_VISIBILITY_CHECK_INTERVAL_MS);
  }

  function detach() {
    clearTimeout(showTimer);
    clearInterval(connectedCheck);
    triggerObserver?.disconnect();
    triggerObserver = undefined;
    candidate = null;
  }

  document.addEventListener('pointerover', onPointerOver, { signal, capture: true });
  document.addEventListener('pointerout', onPointerOut, { signal, capture: true });
  document.addEventListener('scroll', onScroll, { signal, capture: true, passive: true });

  return () => {
    abort();
    setCandidate(null);
  };
}

function getTrigger(target: EventTarget | null) {
  if (!(target instanceof Element)) {
    return null;
  }
  const trigger = target.closest(triggerSelector);
  return trigger instanceof HTMLElement ? trigger : null;
}

function readTrigger(element: HTMLElement): TooltipTrigger | null {
  const text = element.getAttribute(TOOLTIP_ATTRIBUTE) ?? '';
  const hotkey = element.getAttribute(TOOLTIP_HOTKEY_ATTRIBUTE) || null;
  if (!text && !hotkey) {
    return null;
  }

  const position = positions.find(p => p === element.getAttribute(TOOLTIP_POSITION_ATTRIBUTE));
  return { element, text, hotkey, position: position ?? 'top' };
}

function isPresent(element: HTMLElement) {
  if (!element.isConnected) {
    return false;
  }
  // Not in jsdom.
  return typeof element.checkVisibility !== 'function' || element.checkVisibility();
}
